import React, {memo, useEffect, useState} from "react";
import {useTheme as useThemeMui} from "@mui/material";
import Drawer from "@mui/material/Drawer";
import {AdminKeys, AdminModuleCtgs, AdminModuleRouteUrls, AdminModules, ConfigConstants,} from "../config/AdminConfig";
import {CssVariant, FsVarVariant, IconClass} from "../../common/fusion-ui/config/FusionConfig";
import {checkNull, checkNullStr, dateToTimestamp, getDefStr} from "../../common/fusion-ui/utils/ReactUtils";
import {useConfig} from "../context/ConfigProvider";
import {VariantLabel} from "../../common/fusion-ui";
import {Link} from "react-router-dom";
import {printError, printLog} from "../../app/utils/AppUtils";
import {useToast} from "../../common/fusion-ui/provider/ToastProvider";
import {useMediaQuery} from "@mui/system";
import {ThemedIconButton} from "../../common/components";
import {useTheme} from "../../common/ProjectThemeProvider";
import {getModuleCategory} from "../utils/AdminUtils";

const TAG = "AdminSidebar";
const SbKeys = {
    icon: "icon",
    name: "name",
    route: "route",
    ctg: "category",
    subtitle: "subtitle",
    tag: "tag",
    variant: "variant",
};

/**
 * AdminSidebar is a memoized component that renders a dynamic sidebar for an admin panel.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {boolean} props.drawerOpen - Determines whether the sidebar drawer is open.
 * @param {function} props.onSidebarToggle - Function to handle the toggling of the sidebar drawer.
 * @param {Object} [props.window] - Reference to the global window object.
 * @param {function} props.onMenuItemClick - Function to handle click events on menu items.
 *
 * @returns {JSX.Element} The rendered sidebar component.
 *
 * This component adjusts its style and behavior based on screen resolution using Material-UI's
 * theme and media query hooks. It dynamically lists admin modules in categorized sections and
 * allows for responsive toggling.
 */
const AdminSidebar = memo(({drawerOpen, onSidebarToggle, window, onMenuItemClick}) => {

    const {getConfig, updateConfig} = useConfig();
    // const {screenWidth} = useScreenSize();
    const {showToast, changeToastVariant} = useToast();

    const themeMui = useThemeMui();
    const theme = useTheme();
    const isDrawerResp = useMediaQuery(themeMui.breakpoints.up("md"));


    return (
        <div
            className="mb-5"
            style={{
                flexShrink: isDrawerResp ? 0 : "auto",
                width: isDrawerResp ? ConfigConstants.drawerWidth : "auto",
                marginBottom: "4rem",
            }}>
            <Drawer
                // container={window !== undefined ? () => window.document.body : undefined}
                container={checkNull(window) ? () => window.document.body : undefined}
                variant={isDrawerResp ? "persistent" : "temporary"}
                anchor="left"
                open={drawerOpen}
                onClose={onSidebarToggle}
                sx={{
                    "& .MuiDrawer-paper": {
                        width: ConfigConstants.drawerWidth,
                        color: themeMui.palette.text.primary,
                        backgroundColor: theme.bg,
                        borderRight: "none",
                        paddingBottom: "4rem",
                        [themeMui.breakpoints.up("md")]: {
                            top: "58px",//Menu-bar height
                        },
                        zIndex: 1,
                    },
                }}
                ModalProps={{keepMounted: true}}>
                <div className={"py-2 mt-2 cursor-default"}>
                    <div className={"mb-5x"}>
                        {Object.values(AdminModuleCtgs).map((ctg, ctgIndex) => {
                            return <CategoryItem
                                ctgIndex={ctgIndex}
                                key={ctgIndex}
                                isDrawerResp={isDrawerResp}
                                category={ctg}
                                links={getRoutes().filter(rt => rt[SbKeys.ctg] == ctg)}
                                onMenuClick={(name, route, ctg) => {
                                    log('CategoryItem.onMenuClick.name:', name, ' | route:', route, ' | ctg:', ctg);
                                    updateConfig(AdminKeys.tracker_currPageModule, name);
                                    if (onMenuItemClick) onMenuItemClick(name, route, ctg);
                                }}
                                onMenuItemClick={(name, route, ctg) => {
                                    log('CategoryItem.onMenuItemClick.name:', name, ' | route:', route, ' | ctg:', ctg);
                                    updateConfig(AdminKeys.tracker_currPageModule, name);
                                    // updateConfig(AdminKeys.tracker_currPageModule);
                                    if (onMenuItemClick) onMenuItemClick(name, route, ctg);
                                }}
                                currPageModule={getConfig(AdminKeys.tracker_currPageModule)}
                            />;
                        })}
                        <div></div>
                    </div>
                </div>
            </Drawer>
        </div>
    );

    function getRoutes() {
        return [
            createSingleRoute({
                module: AdminModules.Dashboard,
                ctg: AdminModuleCtgs.NoCtg,
                icon: "bi bi-speedometer2",
                // tag: "New",
                tag: dateToTimestamp(new Date()) <= dateToTimestamp('2024-12-31') ? "New" : "",
                variant: CssVariant.info,
            }),

            //MasterMgmt
            createSingleRoute({
                module: AdminModules.ParentCompany,
                ctg: AdminModuleCtgs.MasterMgmt,
                icon: "bi bi-buildings",
            }),
            createSingleRoute({
                module: AdminModules.Company,
                ctg: AdminModuleCtgs.MasterMgmt,
                icon: "bi bi-building",
            }),
            createSingleRoute({
                module: AdminModules.Members,
                ctg: AdminModuleCtgs.MasterMgmt,
                icon: "bi bi-person-rolodex",
            }),
            createSingleRoute({
                module: AdminModules.Users,
                ctg: AdminModuleCtgs.MasterMgmt,
                icon: "bi bi-person-circle",
            }),
            createSingleRoute({
                module: AdminModules.UserRoles,
                ctg: AdminModuleCtgs.MasterMgmt,
                icon: "bi bi-person-bounding-box",
            }),
            createSingleRoute({
                module: AdminModules.Groups,
                ctg: AdminModuleCtgs.MasterMgmt,
                icon: "bi bi-collection",
            }),
            createSingleRoute({
                module: AdminModules.Country,
                ctg: AdminModuleCtgs.MasterMgmt,
                icon: "bi bi-globe-central-south-asia",
            }),
            createSingleRoute({module: AdminModules.State, ctg: AdminModuleCtgs.MasterMgmt, icon: "bi bi-map"}),
            createSingleRoute({module: AdminModules.City, ctg: AdminModuleCtgs.MasterMgmt, icon: "bi bi-geo-fill"}),
            createSingleRoute({
                module: AdminModules.Reports,
                ctg: AdminModuleCtgs.MasterMgmt,
                icon: "bi bi-card-text",
            }),
            createSingleRoute({
                module: AdminModules.ModuleAllocation,
                ctg: AdminModuleCtgs.MasterMgmt,
                icon: "fa fa-columns",
            }),
            createSingleRoute({
                module: AdminModules.LoginBypassManager,
                ctg: AdminModuleCtgs.MasterMgmt,
                icon: "bi bi-box-arrow-in-down-right",
                // icon: "bi bi-box-arrow-in-right",
            }),

            //ActivityMgmt
            createSingleRoute({module: AdminModules.Wm, ctg: AdminModuleCtgs.ActivityMgmt, icon: "bi bi-webcam"}),
            createSingleRoute({
                module: AdminModules.Meeting,
                ctg: AdminModuleCtgs.ActivityMgmt,
                icon: "bi bi-people",
            }),
            createSingleRoute({
                module: AdminModules.Forum,
                ctg: AdminModuleCtgs.ActivityMgmt,
                icon: "bi bi-ubuntu"
            }),
            createSingleRoute({
                module: AdminModules.Survey,
                ctg: AdminModuleCtgs.ActivityMgmt,
                icon: "bi bi-list-nested",
            }),

            //ResourceMgmt
            createSingleRoute({
                module: AdminModules.Pub,
                ctg: AdminModuleCtgs.ResourceMgmt,
                icon: "bi bi-file-post",
            }),
            createSingleRoute({
                module: AdminModules.Articles,
                ctg: AdminModuleCtgs.ResourceMgmt,
                icon: "bi bi-file-text",
            }),
            createSingleRoute({
                module: AdminModules.Doc,
                ctg: AdminModuleCtgs.ResourceMgmt,
                // icon: "bi bi-file-earmark-text",
                icon: "fa fa-file-text-o"
            }),
            createSingleRoute({
                module: AdminModules.CS,
                ctg: AdminModuleCtgs.ResourceMgmt,
                icon: "bi bi-file-earmark-medical",
            }),
            createSingleRoute({
                module: AdminModules.Videos,
                ctg: AdminModuleCtgs.ResourceMgmt,
                icon: "bi bi-file-earmark-play",
            }),
            createSingleRoute({module: AdminModules.Images, ctg: AdminModuleCtgs.ResourceMgmt, icon: "bi bi-image"}),
            createSingleRoute({
                module: AdminModules.Links,
                ctg: AdminModuleCtgs.ResourceMgmt,
                icon: "bi bi-link-45deg",
            }),
            createSingleRoute({
                module: AdminModules.ExpertSpeaks,
                ctg: AdminModuleCtgs.ResourceMgmt,
                icon: "bi bi-explicit",
            }),
            createSingleRoute({
                module: AdminModules.News,
                ctg: AdminModuleCtgs.ResourceMgmt,
                icon: "bi bi-newspaper",
            }),

            //UserMgmt
            // createSingleRoute({module: AdminModules.UserRoles, ctg: AdminModuleCtgs.UserMgmt, icon: 'bi bi-person-bounding-box'}),
            // createSingleRoute({module: AdminModules.Users, ctg: AdminModuleCtgs.UserMgmt, icon: 'bi bi-person-circle'}),
            // createSingleRoute({module: AdminModules.Reports, ctg: AdminModuleCtgs.UserMgmt, icon: 'bi bi-card-text'}),

            //DatabaseMgmt
            createSingleRoute({
                module: AdminModules.BackupDb,
                ctg: AdminModuleCtgs.DatabaseMgmt,
                prefix: null,
                icon: "bi bi-database-check",
            }),
            createSingleRoute({
                module: AdminModules.QueryWindow,
                ctg: AdminModuleCtgs.DatabaseMgmt,
                prefix: null,
                icon: "bi bi-window-dock",
            }),
            //bi bi-search
            // createSingleRoute({
            //     module: AdminModules.DbMaintenance,
            //     ctg: AdminModuleCtgs.DatabaseMgmt,
            //     prefix: null,
            //     icon: "bi bi-database-fill-gear",
            // }),
        ];
    }

    function createSingleRoute({module, url, ctg, tag, variant, prefix = "", icon}) {
        return {
            [SbKeys.name]: checkNullStr(prefix) ? `${prefix} ${module}` : module,
            [SbKeys.route]: getDefStr(url, AdminModuleRouteUrls[module]),
            [SbKeys.icon]: icon,
            [SbKeys.ctg]: ctg,
            ...checkNullStr(tag) ? {[SbKeys.tag]: tag, [SbKeys.variant]: variant} : {},
            // ...checkNullStr(icon) ? {[SbKeys.icon]: icon} : {},
        };
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
    }
});

const CategoryItem = React.memo(({
                                     ctgIndex,
                                     isDrawerResp,
                                     category,
                                     links = [],
                                     onMenuItemClick,
                                     currPageModule,
                                 }) => {
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(checkNullStr(currPageModule) ? getModuleCategory(currPageModule) == category : false);

    useEffect(() => {
        // log('useEffect.currPageModule:', currPageModule);
        if (checkNullStr(currPageModule)) {
            //     log('useEffect.currPageModule-found');
            setIsOpen(getModuleCategory(currPageModule) == category);
        }
    }, [currPageModule]);


    const toggleOpen = () => {
        // log('toggleOpen.isOpen:', !isOpen);
        setIsOpen(!isOpen);
    };
    return (
        <div className={`px-2 admin-sb`}>
            {/*expandable-ctg*/}
            {(category != AdminModuleCtgs.NoCtg) && <div
                className={``}>
                <label
                    className={`admin-sb-exp-item text-nowrap cursor-pointer px-3 py-2 d-flex jc-between al-center fd-row flex-nowrap rounded-4 transition position-relative ${(isOpen) ? `active-item` : ""}`}
                >
                    <span>{category}</span>

                    <ThemedIconButton
                        className={"mx-2 p-2 icon-btn"}
                        icon={`${isOpen ? IconClass.angleUp : IconClass.angleDown} exp-icon`}
                        circular={true}
                        borderWidth={0}
                        outlined={true}
                        color={theme.text70}
                        hoverColor={theme.primaryDark}
                        hoverBgColor={theme.primary20}
                        rippleColor={theme.primary30}
                        size={"24px"}
                        iconSize={FsVarVariant.LG}
                        onClick={toggleOpen}
                    />
                </label>
            </div>}

            {/*links: no-ctg-links*/}
            {(category == AdminModuleCtgs.NoCtg)
                ? <div className={"mb-1x"}>{links.map((link, index) => {
                    return <LinkItem
                        index={index}
                        key={index}
                        link={link}
                        currPageModule={currPageModule}
                        isDrawerResp={isDrawerResp}
                        onMenuItemClick={onMenuItemClick}
                        // onItemClick={toggleOpen}
                        onItemClick={() => setIsOpen(false)}
                    />;
                })}</div>
                : <div className={``}
                       style={{
                           opacity: 0,
                           height: 0,
                           transform: "translateY(-5%)",
                           overflow: "hidden",
                           transition: `height 0.3s ease, padding 0.3s ease, opacity 0.3s ease, transform 0.3s ease`,
                           ...isOpen ? {
                               height: "auto",
                               padding: "10px",
                               opacity: 1,
                               transform: "translateY(0)",
                           } : {},
                       }}>
                    {links.map((link, index) => {
                        return <LinkItem
                            index={index}
                            key={index}
                            link={link}
                            currPageModule={currPageModule}
                            isDrawerResp={isDrawerResp}
                            onMenuItemClick={onMenuItemClick}
                            // onItemClick={toggleOpen}
                            onItemClick={() => setIsOpen(true)}
                        />;
                    })}
                </div>
            }
        </div>
    );
});
const LinkItem = React.memo(({
                                 isDrawerResp,
                                 link,
                                 onMenuItemClick,
                                 onItemClick,
                                 currPageModule,
                                 index,
                             }) => {
    return <div key={index} className={`${index == 0 ? "mt-n2" : ""}`}
                style={{
                    // display: 'block',
                    // padding: '5px 10px',
                    // color: '#007bff',
                    // textDecoration: 'none',
                }}>
        <Link to={link[SbKeys.route]} className={"td-none"}
              onClick={() => {
                  if (!isDrawerResp && onMenuItemClick) onMenuItemClick(link[SbKeys.name], link[SbKeys.route], link[SbKeys.ctg]);
                  if (onItemClick) onItemClick(link[SbKeys.name], link[SbKeys.route], link[SbKeys.ctg]);
              }}>
            <div
                className={`admin-sb-link-item px-3 py-2 d-flex jc-between al-center fd-row flex-nowrap rounded-4 transition position-relative ${currPageModule == link[SbKeys.name] ? `active-item` : ``}`}
                // className={`admin-sb-link-item px-3 py-2 d-flex jc-between al-center fd-row flex-nowrap rounded-4 transition position-relative ${currPageModule == link[SbKeys.name] ? `highlight-primary-full text-white highlight-primary-light-full-hover highlight-primary-dark-full-active` : `text-theme-resp highlight-primary-hover text-primary-dark-hover highlight-primary-dark-active`}`}
                style={{height: "40px"}}>
                <div className={`d-flex jc-start al-center fd-row flex-nowrap`}>
                    {/*icon*/}
                    {checkNullStr(link[SbKeys.icon]) &&
                        <div className={`me-3 fs-normal`}>
                            <i className={`${link[SbKeys.icon]}`}/>
                        </div>}

                    {/*name*/}
                    <div className={`text-nowrap`}>{link[SbKeys.name]}</div>
                </div>

                {/*tag*/}
                {checkNullStr(link[SbKeys.tag]) &&
                    <VariantLabel
                        className={"cursor-pointer"}
                        position={"absolute"} right={".45rem"} height={"16px"}
                        variant={getDefStr(link[SbKeys.variant], CssVariant.dark)}
                        roundedVariant={10}>
                        {link[SbKeys.tag]}
                    </VariantLabel>}
            </div>
        </Link>
    </div>;
});


function log(...text) {
    printLog(TAG, ...text);
}

function logErr(...text) {
    printError(TAG, ...text);
}

// AdminSidebar.propTypes = {
//     drawerOpen: PropTypes.bool,
//     onSidebarToggle: PropTypes.func,
//     window: PropTypes.object
// };

export default AdminSidebar;
